/** @format */

import {LockClosedIcon} from '@heroicons/react/solid'
import {useDisclosure} from '@mantine/hooks'
import * as Sentry from '@sentry/react'
import {PaymentMethod} from '@stripe/stripe-js'
import React, {PropsWithChildren} from 'react'
import {Link} from 'react-router-dom'

import {StripeElementsProvider} from '@src/components/tailwind/StripeElementsProvider'
import {UpgradeSubscriptionModal} from '@src/components/tailwind/UpgradeSubscriptionModal'

import {AnalyticsEvent, sendAnalytics} from '@src/analytics'
import {
  FeatureId,
  Plan,
  useAccountContext,
  useMinimumUpgradeProduct,
  useProductsApi,
} from '@src/contexts'
import {useChangePlan} from '@src/hooks/useChangePlan'
import {PLUGIN_CONFIGS, pluginRequires} from '@src/plugins/pluginConfigs'
import type {PluginKind} from '@src/types/Plugin'

// What the upgrade is for: a feature, or a plugin, never both.
//
// The type forbids both rather than merely discouraging it. With both, which
// plan the reader gets offered would depend on which one won, and only a
// hand-typed URL should be able to create that situation.
export type UpgradeTarget =
  {feature: FeatureId; plugin?: never} | {feature?: never; plugin: PluginKind}

type UpgradeLinkProps = PropsWithChildren<
  {
    // extra properties to record on the "Upgrade Clicked" analytics event
    analyticsProperties?: Record<string, unknown>
    className?: string
    overrideFeatureName?: string
  } & UpgradeTarget
>

// The feature this upgrade is gated on, which decides both the plan we
// recommend and the copy naming it. A feature target is its own answer; a plugin
// target resolves to whatever that plugin requires. UpgradeMessage calls this
// too, to name the plan in its own text.
//
// Returns undefined for a plugin the catalogue does not gate — it is on every
// plan, so there is no upgrade to offer. Callers render nothing rather than
// guess a plan. Reported, not thrown: asking for an upgrade link to an ungated
// plugin is a caller bug, but not one worth blanking the form editor over.
export function resolveGatingFeature(
  target: UpgradeTarget,
): FeatureId | undefined {
  const feature =
    target.feature ??
    (target.plugin === undefined ? undefined : pluginRequires(target.plugin))
  if (feature === undefined) {
    Sentry.captureException(
      new Error(
        `UpgradeLink has no upgradeable target: ${JSON.stringify(target)}`,
      ),
    )
  }
  return feature
}

// UpgradeLink dispatches to one of two independent paths. They diverge in
// both rendering and analytics, so each lives in its own component:
//   - UpgradePlanV2 (the experiment): opens a checkout modal in place
//   - UpgradePlan (the default): a plain link to the plans page
export function UpgradeLink(props: UpgradeLinkProps) {
  const {account} = useAccountContext()

  if (account.flags.upgrade_plan_v2) {
    return (
      <StripeElementsProvider>
        <UpgradePlanV2 {...props} />
      </StripeElementsProvider>
    )
  }

  return <UpgradePlan {...props} />
}

// Default path: names the cheapest qualifying plan, deep-links to it, and
// records recommended_product on the analytics event.
function UpgradePlan(props: UpgradeLinkProps) {
  const {analyticsProperties, children, className = 'primaryLink'} = props
  const feature = resolveGatingFeature(props)

  const product = useMinimumUpgradeProduct(feature)

  if (feature === undefined) {
    return null
  }

  // Build the link's query string. Send what names the row, never the plan: the
  // page works the plan out from the same feature with the same function, and a
  // URL pairing a real feature with an unrelated real plan is how you end up
  // advertising the wrong thing.
  //
  // Send ?plugin= only when the plans table has a row for that plugin, meaning
  // it is in the shared catalogue. Redshift is not, so ?plugin=redshift would
  // mark nothing and name nothing; send the feature it needs instead, which
  // still moves the pill.
  const params = new URLSearchParams()
  if (props.feature) {
    params.set('feature', props.feature)
  }
  if (props.plugin) {
    const hasRow = PLUGIN_CONFIGS.some(config => config.kind === props.plugin)
    if (hasRow) {
      params.set('plugin', props.plugin)
    } else {
      params.set('feature', feature)
    }
  }

  function handleClick() {
    sendAnalytics(AnalyticsEvent.upgrade_clicked, {
      ...analyticsProperties,
      feature,
      recommended_product: product?.id,
    })
  }

  return (
    <Link className={className} onClick={handleClick} to={`/plans?${params}`}>
      {children ?? <DefaultChildren productName={product?.name} />}
    </Link>
  )
}

// V2 path (experiment): opens the in-place checkout modal. Its analytics
// payload intentionally omits recommended_product — keep this shape stable
// while the experiment is running.
//
// A separate component is required to conditionally call useChangePlan.
function UpgradePlanV2(props: UpgradeLinkProps) {
  const {
    analyticsProperties,
    children,
    className = 'primaryLink',
    overrideFeatureName,
  } = props
  const feature = resolveGatingFeature(props)

  const productsApi = useProductsApi()
  const changePlan = useChangePlan()
  const [isModalOpen, modal] = useDisclosure(false)

  if (feature === undefined) {
    return null
  }

  // Below the guard on purpose: getFeatureById throws on an id we do not sell.
  const f = productsApi.getFeatureById(feature)
  const products = productsApi.getProductsWithFeature(feature)
  const productNames = products.map(id => productsApi.getProductById(id).name)

  function handleUpgradeClick() {
    modal.open()
    sendAnalytics(AnalyticsEvent.upgrade_clicked, {
      ...analyticsProperties,
      feature,
    })
  }

  async function handleConfirmUpgrade(
    plan: Plan,
    paymentMethod?: PaymentMethod,
  ): Promise<void> {
    await changePlan(plan, paymentMethod)
    close()
  }

  const featureName = overrideFeatureName ?? f.name
  const description = formatUpgradeModalDescription(featureName, productNames)
  return (
    <>
      <button className={className} onClick={handleUpgradeClick} type="button">
        {children ?? <DefaultChildren />}
      </button>

      <UpgradeSubscriptionModal
        description={description}
        isOpen={isModalOpen}
        onClose={() => modal.close()}
        onConfirmUpgrade={handleConfirmUpgrade}
        options={products}
        title={`Upgrade to unlock ${featureName}`}
      />
    </>
  )
}

function DefaultChildren(props: {productName?: string}) {
  const {productName} = props
  return (
    <>
      <LockClosedIcon className="inline-block h-4 w-4 align-text-bottom" />
      <span>
        &nbsp;{productName ? `Upgrade to ${productName}` : 'Upgrade to unlock'}
      </span>
    </>
  )
}

function formatUpgradeModalDescription(
  featureName: string,
  productNames: string[],
): string {
  const [p1, p2, p3] = productNames
  const prefix = `${featureName} requires a subscription`
  switch (productNames.length) {
    case 1:
      return `${prefix} to a ${p1} plan.`
    case 2:
      return `${prefix} to a ${p1} or ${p2} plan.`
    case 3:
      return `${prefix} to a ${p1}, ${p2} or ${p3} plan.`
    default:
      Sentry.captureException(
        new Error(
          `Encounter incorrect number of products (${productNames.length})`,
        ),
        {
          tags: {
            names: productNames.join(','),
          },
        },
      )
      return `${prefix}.`
  }
}
